import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { api, ApiError } from "../../shared/api/client";
import { Button, Drawer, Field, InlineAlert, LoadingState, Select, Textarea, cn } from "../../shared/ui";
import type {
  EvidenceView,
  HistoryView,
  MigrationDetailView,
  ReviewState,
  TransitionView,
  WorkItemStatus,
  WorkItemTransitionReason,
  WorkItemType,
} from "../../shared/types/api";

import {
  EVIDENCE_PHRASES,
  TRANSITION_REASONS,
  REASON_REQUIRING_COMMENT,
  STATUS_LABELS,
  formatTimestamp,
  missingForTesting,
  needsReview,
  outstandingEvidence,
} from "./workItemVocabulary";

/**
 * Enough of an item to move it.
 *
 * <p>Narrower than WorkItemDetail on purpose: the board holds summaries and the detail page holds
 * details, and both move items the same way. Asking for the whole detail would force the board to
 * fetch one per card for a drawer that shows three fields.
 */
export interface MovableItem {
  id: string;
  itemKey: string;
  title: string;
  status: WorkItemStatus;
  /**
   * What the item owes before it can be completed (§15), when the caller happens to know.
   *
   * <p>Optional because the board holds summaries, which carry no evidence — and a caller that
   * cannot answer the question must not be made to guess at it. When these are absent the
   * drawer simply offers Complete and lets the server have the last word, which is what it did
   * everywhere before this.
   */
  workItemType?: WorkItemType;
  evidence?: EvidenceView[];
  migrationDetail?: MigrationDetailView | null;
  /** What Start testing needs before the server will allow it — see {@link missingForTesting}. */
  testerId?: string | null;
  testingDueDate?: string | null;
  /** True while work returned from QA still owes a new development due date. */
  awaitingReworkPlan?: boolean;
  /**
   * Whether anybody has read the code yet, when the caller knows.
   *
   * <p>Optional on the same terms as the evidence above: a caller that cannot answer must not be
   * made to guess. Where they are absent, Pass QA simply says nothing about the review — which
   * is what it did before, and is better than a warning built on a value nobody supplied.
   */
  reviewState?: ReviewState;
  devCompletedDate?: string | null;
}

/**
 * What still has to be produced before Complete will work, or nothing when it is not this
 * item's question and nothing when the caller did not bring enough to answer it.
 *
 * <p>The spec is explicit that the Complete action should state what is outstanding rather than
 * fail when pressed: an item that cannot be completed should say so before somebody tries. It
 * asks {@link outstandingEvidence}, the same function the evidence panel asks, so the panel and
 * this button can never name different things.
 */
function stillOwed(item: MovableItem | null): ReturnType<typeof outstandingEvidence> {
  if (!item?.workItemType || !item.evidence) {
    return [];
  }
  return outstandingEvidence({
    workItemType: item.workItemType,
    evidence: item.evidence,
    migrationDetail: item.migrationDetail,
  });
}

/**
 * "queries and a screenshot" — the missing things as a person would say them, matching the
 * server's own wording (see {@link EVIDENCE_PHRASES}) so the refusal never disagrees with itself.
 */
function inWords(kinds: ReturnType<typeof outstandingEvidence>): string {
  const names = kinds.map((kind) => EVIDENCE_PHRASES[kind]);
  const last = names[names.length - 1] ?? "";
  if (names.length <= 1) {
    return last;
  }
  return `${names.slice(0, -1).join(", ")} and ${last}`;
}


/**
 * Moves an item along the board (§51), and records why when the move goes backwards.
 *
 * <p>The actions offered come from the server rather than from a list here, because the server is
 * what enforces them. A drawer that offered every status and let the API refuse most of them would
 * teach people to guess, and the two lists would drift the first time the workflow changed.
 *
 * <p>Backward moves open a second step asking for a reason. That step is deliberately not skippable
 * and deliberately not a free-text box alone: §59's coded list is what makes "how often does work
 * come back, and why" answerable, and the sentence beside it is what makes one instance
 * understandable.
 */
export function WorkItemStatusDrawer({
  item,
  preset,
  onClose,
  onSaved,
  onSetUpTesting,
  onReplan,
}: {
  item: MovableItem | null;
  /** Chosen already — dragging a card names the move, so the drawer opens on its reason step. */
  preset?: TransitionView | null;
  onClose: () => void;
  onSaved: () => void;
  /**
   * Opens the Testing panel, when the caller has one to open.
   *
   * <p>Without it the refusal below sends people to a panel this drawer is sitting on top of:
   * on the item page the Testing panel is in the right-hand column, exactly where the drawer
   * opens. Telling somebody to go somewhere they cannot see is not an instruction.
   */
  onSetUpTesting?: () => void;
  /** Opens the Dates panel, for an item that came back from QA and owes a new due date. */
  onReplan?: () => void;
}) {
  const [chosen, setChosen] = useState<TransitionView | null>(null);
  const [reason, setReason] = useState<WorkItemTransitionReason | "">("");
  const [comment, setComment] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();

  /*
   * Keyed on the item alone, never on the status the caller happens to be holding. The board
   * passes its own cached summary, so keying on `item.status` meant a board that had not yet
   * refetched resolved the key to a status the item had already left — and React Query answered
   * from the cache it had filled under that key rather than asking the server. The drawer then
   * offered "Pass QA" on an item that was back in progress: the server refused it, which is the
   * one thing this drawer exists to make impossible, since the actions offered are supposed to
   * come from the same place the rules are enforced.
   */
  const transitions = useQuery({
    queryKey: ["work-item-transitions", item?.id],
    queryFn: () => api.get<TransitionView[]>(`/api/v1/work-items/${item!.id}/transitions`),
    enabled: item != null,
    /* Always re-asked when the drawer opens. The default 30s would otherwise answer from cache
       for an item somebody else has moved in the meantime, which is the same wrong list by a
       different route. It is one small request, made only when a drawer is actually opened. */
    staleTime: 0,
  });

  /* Deciding where something goes next is hard without knowing where it has just been. The same
     query key the activity feed uses, so opening this drawer on the item's own page costs
     nothing and the two can never show different answers. */
  const history = useQuery({
    queryKey: ["work-item-history", item?.id],
    queryFn: () => api.get<HistoryView[]>(`/api/v1/work-items/${item!.id}/history`),
    enabled: item != null,
  });

  const lastMove = history.data?.find((entry) => entry.eventType === "STATUS_CHANGED") ?? null;

  useEffect(() => {
    if (item) {
      setChosen(preset ?? null);
      setReason("");
      setComment("");
      setError(null);
    }
  }, [item, preset]);

  const commit = async (transition: TransitionView, code: WorkItemTransitionReason | "") => {
    setSaving(true);
    setError(null);
    try {
      await api.post(`/api/v1/work-items/${item!.id}/transitions`, {
        transition: transition.transition,
        reason: code === "" ? null : code,
        reasonComment: comment.trim() === "" ? null : comment.trim(),
      });
      /* Both callers refresh what they show, but only one of them shows history. Invalidating
         here means the panel above is right whichever screen opened this. */
      void queryClient.invalidateQueries({ queryKey: ["work-item-history", item!.id] });
      /* The move is exactly what changes the answer to "what can be done from here", so the
         cached one is wrong the moment this succeeds. It used to survive, because nothing
         invalidated it and the key carried a status the caller was about to stop believing. */
      void queryClient.invalidateQueries({ queryKey: ["work-item-transitions", item!.id] });
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not move that item.");
    } finally {
      setSaving(false);
    }
  };

  /*
   * Choosing an action never commits it. It used to: clicking a move that needed no reason
   * applied it and closed the drawer in one go, so a misclick changed the item instantly, and
   * the only remedy was another recorded move. One deliberate confirmation is cheap; an
   * accidental transition is permanent, because history cannot be erased.
   */
  const pick = (transition: TransitionView) => {
    setChosen(transition);
    setError(null);
  };

  const needsComment = reason !== "" && REASON_REQUIRING_COMMENT.includes(reason);
  /* Only COMPLETE_WORK is held back by evidence. Every other move is available to an item with
     nothing attached, which is the whole point of collecting it at the end rather than up front. */
  const outstanding = stillOwed(item);
  const blockedByEvidence = outstanding.length > 0;
  const testingMissing = item ? missingForTesting(item) : [];
  const blockedByTesting = testingMissing.length > 0;
  const blockedByReplan = item?.awaitingReworkPlan === true;
  const passingUnreviewed = item?.workItemType != null
    && needsReview({
      workItemType: item.workItemType,
      reviewState: item.reviewState,
      devCompletedDate: item.devCompletedDate,
    });
  const canConfirm = chosen != null
    && !(chosen.transition === "COMPLETE_WORK" && blockedByEvidence)
    && !(chosen.transition === "START_TESTING" && blockedByTesting)
    && !(chosen.transition === "SEND_TO_CODE_REVIEW" && blockedByReplan)
    && (!chosen.requiresReason || (reason !== "" && (!needsComment || comment.trim() !== "")));

  return (
    <Drawer
      open={Boolean(item)}
      onClose={onClose}
      title={chosen ? chosen.label : "Move this item"}
      subtitle={item ? `${item.itemKey} — ${item.title}` : undefined}
      footer={
        <div className="flex justify-between gap-2">
          {chosen && !preset ? (
            <Button
              variant="ghost"
              icon={<ArrowLeft className="h-4 w-4" />}
              onClick={() => {
                setChosen(null);
                setError(null);
              }}
              disabled={saving}
            >
              Back
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose} disabled={saving}>
              {chosen ? "Cancel" : "Close"}
            </Button>
            {chosen && (
              <Button
                onClick={() => void commit(chosen, reason)}
                disabled={!canConfirm || saving}
              >
                {chosen.label}
              </Button>
            )}
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        {chosen ? (
          <>
            <p className="text-sm text-ink-600">{chosen.consequence}</p>

            {chosen.transition === "COMPLETE_WORK" && blockedByEvidence && (
              <InlineAlert tone="warning">
                This cannot be completed yet. It still needs {inWords(outstanding)} — provide it on
                the Evidence panel of this item, and the work can be closed from here.
              </InlineAlert>
            )}

            {chosen.transition === "START_TESTING" && blockedByTesting && (
              <InlineAlert tone="warning">
                <div className="space-y-2">
                  <p>
                    Testing cannot start yet. It still needs {testingMissing.join(" and ")}
                    {onSetUpTesting
                      ? "."
                      : " — add them from the Testing panel of this item."}
                  </p>
                  {onSetUpTesting && (
                    <Button size="sm" variant="secondary" onClick={onSetUpTesting}>
                      Set the tester and date
                    </Button>
                  )}
                </div>
              </InlineAlert>
            )}

            {/* Not a blocker. Finishing unreviewed is allowed, and refusing it stopped nothing
                real; what it cost was a tester sitting on a verdict they had already reached.
                The one thing worth doing is making sure nobody passes it without knowing what
                the record will then say about it. */}
            {chosen.transition === "PASS_QA" && passingUnreviewed && (
              <InlineAlert tone="warning">
                Nobody has read this code yet. Passing is allowed, and the item will say it was
                tested unreviewed — and once a reviewer approves it, it will ask to be tested
                again, because the testing will not describe the code they approved.
              </InlineAlert>
            )}

            {chosen.transition === "SEND_TO_CODE_REVIEW" && blockedByReplan && (
              <InlineAlert tone="warning">
                <div className="space-y-2">
                  <p>
                    This came back from QA, and the date on it is the one agreed for the round that
                    has already finished. Give the rework a new development due date
                    {onReplan ? "." : " from the item's Dates panel."}
                  </p>
                  {onReplan && (
                    <Button size="sm" variant="secondary" onClick={onReplan}>
                      Set a new due date
                    </Button>
                  )}
                </div>
              </InlineAlert>
            )}

            {lastMove && <LastMove entry={lastMove} />}

            {chosen.requiresReason ? (
              <InlineAlert tone="info">
                This move takes the work backwards, so it is recorded with a reason. That record is
                the only thing that can explain it later.
              </InlineAlert>
            ) : (
              <p className="text-sm text-ink-600">
                Confirm to move this item. It is recorded against your name, and putting it back
                afterwards is another recorded move rather than an undo.
              </p>
            )}

            {chosen.requiresReason && (
            <Field label="Reason" required>
              <Select
                value={reason}
                autoFocus
                onChange={(event) =>
                  setReason(event.target.value as WorkItemTransitionReason | "")
                }
              >
                <option value="">Choose a reason…</option>
                {TRANSITION_REASONS.map(([code, label]) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
              </Select>
            </Field>
            )}

            {chosen.requiresReason && (
            <Field
              label="What happened"
              required={needsComment}
              hint={
                needsComment
                  ? "Required, because “Something else” counts as nothing on its own."
                  : "Optional, and kept with the item for good."
              }
            >
              <Textarea
                rows={3}
                value={comment}
                onChange={(event) => setComment(event.target.value)}
              />
            </Field>
            )}
          </>
        ) : (
          <>
            <p className="text-sm text-ink-500">
              Every move is recorded with who made it and when, which is what makes a slipped item
              explainable afterwards.
            </p>

            {lastMove && <LastMove entry={lastMove} />}

            {transitions.isPending ? (
              <LoadingState label="Working out what can be done" />
            ) : transitions.isError ? (
              <InlineAlert tone="error">Could not load what can be done from here.</InlineAlert>
            ) : (
              <div className="space-y-1.5">
                {transitions.data.map((transition) => (
                  <button
                    key={transition.transition}
                    type="button"
                    disabled={saving}
                    onClick={() => pick(transition)}
                    className={cn(
                      "flex w-full items-start justify-between gap-3 rounded-lg border px-3 py-2.5",
                      "text-left transition-colors border-ink-200",
                      "hover:border-ink-300 hover:bg-ink-50",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400",
                      "disabled:cursor-not-allowed disabled:opacity-60",
                    )}
                  >
                    <span>
                      <span className="block text-sm font-medium text-ink-900">
                        {transition.label}
                      </span>
                      <span className="mt-0.5 block text-xs text-ink-600">
                        {transition.consequence}
                      </span>
                    </span>
                    {/*
                      * Said before the click, not after it. Both of these are refused by the
                      * server; a button that looks available and then explains why it was not
                      * teaches people to guess.
                      */}
                    {transition.requiresPlan && (
                      <span className="shrink-0 rounded-md bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800">
                        Needs an estimate and dates
                      </span>
                    )}
                    {transition.transition === "COMPLETE_WORK" && blockedByEvidence && (
                      <span className="shrink-0 rounded-md bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800">
                        Needs {inWords(outstanding)}
                      </span>
                    )}
                    {transition.transition === "START_TESTING" && blockedByTesting && (
                      <span className="shrink-0 rounded-md bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800">
                        Needs {testingMissing.join(" and ")}
                      </span>
                    )}
                    {transition.transition === "SEND_TO_CODE_REVIEW" && blockedByReplan && (
                      <span className="shrink-0 rounded-md bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800">
                        Needs a new due date
                      </span>
                    )}
                    {transition.requiresReason && (
                      <span className="shrink-0 rounded-md bg-ink-100 px-1.5 py-0.5 text-xs font-medium text-ink-700">
                        Needs a reason
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Drawer>
  );
}

/**
 * Where this item has just been, shown beside where it can go next.
 *
 * <p>Choosing the next move without seeing the last one is how an item gets sent back to
 * development twice for the same thing. The reason is included rather than summarised: "Fail QA"
 * and "Fail QA — the totals were still wrong for part-time staff" lead to different decisions,
 * and the second one is the whole point of having asked.
 *
 * <p>Entries predating the workflow have no transition name, so they render as the plain columns
 * they were recorded as. Inventing a name for them would put a claim into the record that nobody
 * actually made.
 */
function LastMove({ entry }: { entry: HistoryView }) {
  /*
   * "Now" comes from this row rather than from the item passed in. The last status change's new
   * value *is* the current status, so the two halves of the line cannot contradict each other —
   * and the props can be a moment stale when somebody else has just moved the same item, which
   * is exactly when a reader most needs the panel to be self-consistent.
   */
  const now = entry.newValue as WorkItemStatus | null;
  return (
    /* Tinted and accented rather than another grey card. Sitting directly above the actions in
       identical styling, it read as a fourth thing you could click; the stripe and the brand
       ground say "this is context, not a choice" before anybody reads a word of it.

       brand-50, brand-500 and brand-700 are all redefined for dark mode. brand-200 is not, so it
       is deliberately avoided here — it would keep its light-mode lilac on a dark card. */
    <div className="rounded-lg border border-l-4 border-brand-100 border-l-brand-500 bg-brand-50 px-3 py-2.5">
      <p className="text-xs font-semibold uppercase tracking-wide text-brand-700">Last move</p>

      <p className="mt-1 text-sm text-ink-900">
        {entry.transitionLabel ?? (
          <>
            {entry.oldValue != null && STATUS_LABELS[entry.oldValue as WorkItemStatus] != null
              ? STATUS_LABELS[entry.oldValue as WorkItemStatus]
              : entry.oldValue}
            {" → "}
            {entry.newValue != null && STATUS_LABELS[entry.newValue as WorkItemStatus] != null
              ? STATUS_LABELS[entry.newValue as WorkItemStatus]
              : entry.newValue}
          </>
        )}
        {now != null && STATUS_LABELS[now] != null && (
          <span className="font-normal text-ink-600">
            {" · now "}
            {STATUS_LABELS[now]}
          </span>
        )}
      </p>

      {entry.reasonLabel != null && (
        <p className="mt-1 text-xs text-ink-700">
          <span className="font-medium">{entry.reasonLabel}</span>
          {entry.reasonComment != null && ` — ${entry.reasonComment}`}
        </p>
      )}

      {/* ink-600, not ink-500: measured 4.19:1 against the brand tint this panel now sits on,
          which was fine on the grey it replaced and is below AA here. */}
      <p className="mt-1 text-xs text-ink-600">
        {entry.actorName ?? "The system"} · {formatTimestamp(entry.eventTimestamp)}
      </p>
    </div>
  );
}
